import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(identifier, password);
            toast.success("Welcome back!");
            const to = loc.state?.from || "/app/dashboard";
            nav(to);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            <div className="hidden md:flex flex-col justify-between p-12 bg-slate-900 text-white">
                <Link to="/" className="flex items-center gap-2 font-display font-black text-xl">
                    <span className="w-8 h-8 rounded-lg bg-[#2563EB] grid place-items-center">B</span>
                    Zenwyxon
                </Link>
                <div>
                    <h1 className="font-display font-black text-5xl tracking-tighter">Welcome back, networker.</h1>
                    <p className="mt-6 text-slate-300 text-lg max-w-md">Your tree kept growing while you were gone. Let's see how many pairs are waiting.</p>
                </div>
                <div className="text-sm text-slate-500">© 2026 Zenwyxon</div>
            </div>
            <div className="flex items-center justify-center p-8 md:p-12 bg-white">
                <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
                    <div className="overline mb-4">Log in</div>
                    <h2 className="font-display font-black text-3xl tracking-tighter text-slate-900">Sign in to your account</h2>
                    <p className="mt-2 text-slate-500 text-sm">Sign in with your email address or Member ID.</p>

                    <label className="block mt-8">
                        <span className="text-sm font-semibold text-slate-700">Email or Member ID</span>
                        <input
                            data-testid="login-email"
                            type="text"
                            required
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            className="mt-2 w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="you@example.com  or  ABC12345"
                        />
                        <span className="mt-1 text-[11px] text-slate-400 block">
                            Use your email address or your 8-character Member ID (e.g. VJMRBE55)
                        </span>
                    </label>
                    <label className="block mt-5">
                        <span className="text-sm font-semibold text-slate-700">Password</span>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none"
                        />
                    </label>

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="mt-8 w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Log in
                    </button>

                    <p className="mt-6 text-sm text-slate-600">
                        Don't have an account?{" "}
                        <Link to="/register" className="font-semibold text-[#2563EB] hover:underline">Register</Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
